/**
 * Suche: Vorschläge beim Absenden sofort ausblenden.
 *
 * Problem: Die theme-eigene <predictive-search> (theme.js) startet die Vorschlags-Anfrage per
 * Debounce (280 ms) nach dem letzten Tastendruck und bricht sie beim Absenden des Formulars
 * nicht ab. Wer schnell tippt und Enter drückt, sieht in der Ladezeit der Suchseite noch die
 * nachträglich eingetroffenen Vorschläge aufblitzen.
 *
 * Lösung (ohne Eingriff in theme.js): Beim Absenden eines Suchformulars werden die Vorschläge
 * geleert und der umgebende Dialog (Such-Overlay bzw. Menü-Drawer) geschlossen. Ein
 * MutationObserver hält den Ergebnis-Container leer, solange die Navigation läuft — die
 * nachlaufende Antwort der Vorschlags-Suche wird so unterdrückt. Zurückgesetzt wird bei
 * erneuter Eingabe oder wenn die Seite aus dem Back/Forward-Cache zurückkommt.
 *
 * Deckt alle drei Sucheingaben des Headers ab (Such-Dialog, Inline-Suche Desktop, Suchleiste im
 * mobilen Menü), da alle dasselbe Muster nutzen: <form role="search" aria-owns="{id}"> zeigt
 * auf die zugehörige <predictive-search id="{id}">.
 */
const SEARCH_FORM_SELECTOR = 'form[role="search"][aria-owns]';
const RESULTS_SLOT_SELECTOR = '[slot="results"]';
const DIALOG_SELECTOR = 'header-search, header-sidebar';

/** Aktive Sperren pro Ergebnis-Container (WeakMap → keine Leaks bei Re-Renders). */
const guards = new WeakMap();

function resultsFor(form) {
  const results = document.getElementById(form.getAttribute('aria-owns'));

  return results?.querySelector(RESULTS_SLOT_SELECTOR) ?? null;
}

function suppress(resultsSlot) {
  if (guards.has(resultsSlot)) {
    return;
  }

  resultsSlot.replaceChildren();

  const observer = new MutationObserver(() => {
    if (resultsSlot.childNodes.length > 0) {
      resultsSlot.replaceChildren();
    }
  });

  observer.observe(resultsSlot, { childList: true });
  guards.set(resultsSlot, observer);
}

function release(resultsSlot) {
  guards.get(resultsSlot)?.disconnect();
  guards.delete(resultsSlot);
}

function onSubmit(event) {
  const form = event.target;

  // Bubble-Phase: theme.js hat die leere Suche bereits per preventDefault gestoppt.
  if (event.defaultPrevented || !(form instanceof HTMLFormElement) || !form.matches(SEARCH_FORM_SELECTOR)) {
    return;
  }

  const resultsSlot = resultsFor(form);

  if (resultsSlot) {
    suppress(resultsSlot);
  }

  form.closest(DIALOG_SELECTOR)?.hide?.();
}

/* Erneute Eingabe (z. B. nach abgebrochener Navigation) hebt die Sperre wieder auf. */
function onInput(event) {
  const form = event.target.form;

  if (!form?.matches(SEARCH_FORM_SELECTOR)) {
    return;
  }

  const resultsSlot = resultsFor(form);

  if (resultsSlot) {
    release(resultsSlot);
  }
}

/* Rückkehr aus dem Back/Forward-Cache: alle Sperren lösen, Vorschläge dürfen wieder erscheinen. */
function onPageShow(event) {
  if (!event.persisted) {
    return;
  }

  document.querySelectorAll(SEARCH_FORM_SELECTOR).forEach((form) => {
    const resultsSlot = resultsFor(form);

    if (resultsSlot) {
      release(resultsSlot);
    }
  });
}

document.addEventListener('submit', onSubmit);
document.addEventListener('input', onInput);
window.addEventListener('pageshow', onPageShow);
